import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { selectWindow } from "../stream-cache/select-window";
import { type CachedStream, getStreamCacheKey, useStreamCacheStore } from "../stream-cache/stream-cache-store";
import type { RequestedStream, WindowedStreams } from "./types";

type StreamCacheSlice = Record<string, CachedStream | undefined>;
type StreamCacheState = ReturnType<typeof useStreamCacheStore.getState>;

function buildStreamCacheSlice(
    streamCache: StreamCacheState["streamCache"],
    lookupKeys: ReturnType<typeof getStreamCacheKey>[],
): StreamCacheSlice {
    return Object.fromEntries(lookupKeys.map((key) => [key, streamCache[key]]));
}

/** Reads this panel's cache and window-selects points. See README.md. */
export function useStreamData({
    streams,
    startMs,
    endMs,
}: {
    streams: RequestedStream[];
    startMs: number;
    endMs: number;
}): WindowedStreams {
    const streamsKey = streams
        .map((s) => `${s.streamId}:${s.resolution}`)
        .sort()
        .join(",");

    const lookups = useMemo(
        () =>
            streams.map(({ streamId, resolution }) => ({
                streamId,
                resolution,
                key: getStreamCacheKey(streamId, resolution),
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- streamsKey stabilizes streams
        [streamsKey],
    );

    const selector = useCallback(
        (state: StreamCacheState) => ({
            // Tag with the lookups — useShallow can't tell two all-undefined slices apart otherwise.
            _lookupId: streamsKey,
            ...buildStreamCacheSlice(
                state.streamCache,
                lookups.map((lookup) => lookup.key),
            ),
        }),
        [lookups, streamsKey],
    );

    const sliceWithSentinel = useStreamCacheStore(useShallow(selector));

    // Drop the tag — only needed for the shallow compare above.
    const slice = useMemo<StreamCacheSlice>(() => {
        const { _lookupId: _sentinel, ...rest } = sliceWithSentinel;
        return rest;
    }, [sliceWithSentinel]);

    // Re-slice when the user pans (startMs/endMs). Cache subscription stays the same.
    return useMemo(() => {
        const data: WindowedStreams = {};
        lookups.forEach(({ streamId, resolution, key }) => {
            const entry = slice[key];
            if (entry) {
                data[streamId] = { points: selectWindow(entry.points, startMs, endMs), resolution };
            }
        });
        return data;
    }, [slice, startMs, endMs, lookups]);
}
